import { useCallback, useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { TaxRate } from "../types/taxRates";
import { isTaxRateCurrent } from "../utils/taxRateFormat";

interface UseProductTaxRatesResult {
  /** Current (not end-dated) tax rates linked to the product. */
  taxRates: TaxRate[];
  isLoading: boolean;
  error: string | null;
  /** Re-fetch, e.g. after the product's tax rates were edited. */
  refresh: () => void;
}

/**
 * Loads the tax rates applied to one product.
 *
 * Product reads don't carry their tax linkage, so — like EditProductModal —
 * this reads the location's tax rates (GET /business-locations/{id}/tax-rates)
 * and keeps those whose `product_ids` include the product. Pass `enabled=false`
 * to skip fetching while the consuming UI is closed.
 */
export function useProductTaxRates(
  productId: string | null | undefined,
  enabled = true,
): UseProductTaxRatesResult {
  const { getTaxRates } = useApp();
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled || !productId || !businessInfo?.id) {
      setTaxRates([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const rates: TaxRate[] = await getTaxRates(businessInfo.id);
        if (cancelled) return;
        setTaxRates(
          (Array.isArray(rates) ? rates : []).filter(
            (rate) =>
              rate.product_ids?.includes(productId) && isTaxRateCurrent(rate),
          ),
        );
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load product tax rates:", err);
        setError("Couldn't load tax rates");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, productId, businessInfo?.id, getTaxRates, refreshKey]);

  return { taxRates, isLoading, error, refresh };
}
